import { useState, useRef, useEffect } from "react";
import { Volume2, Play, Pause, Lightbulb, Sparkles, Clock, Mail, Check, Send } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KarineDailyTip = ({ className = "", variant = "card", showBlog = true, showSubscribe = true }) => {
  const { language } = useLanguage();
  const [tip, setTip] = useState(null);
  const [blog, setBlog] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedBlog, setExpandedBlog] = useState(false);
  const audioRef = useRef(null);
  
  // Subscription state
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscribeError, setSubscribeError] = useState("");
  
  // Fetch daily tip and blog
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch tip
        const tipRes = await axios.get(`${API}/tts/daily-tip`);
        setTip(tipRes.data);
        
        // Fetch blog
        if (showBlog) {
          const blogRes = await axios.get(`${API}/blog/daily`);
          setBlog(blogRes.data);
        }
        
        // Setup audio if available
        if (tipRes.data?.audio_url) {
          const fullAudioUrl = tipRes.data.audio_url.startsWith('http') 
            ? tipRes.data.audio_url 
            : `${process.env.REACT_APP_BACKEND_URL}${tipRes.data.audio_url}`;
          const audio = new Audio(fullAudioUrl);
          audioRef.current = audio;
          
          audio.addEventListener("timeupdate", () => {
            setProgress((audio.currentTime / audio.duration) * 100);
          });
          
          audio.addEventListener("ended", () => {
            setIsPlaying(false);
            setProgress(0);
          });
        }
      } catch (err) {
        console.error("Failed to fetch daily content:", err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [showBlog]);
  
  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };
  
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubscribing(true);
    setSubscribeError("");
    
    try {
      const res = await axios.post(`${API}/daily-tips/subscribe`, {
        email: email.trim(),
        first_name: firstName.trim()
      });
      
      if (res.data.success) {
        setSubscribeSuccess(true);
        setEmail("");
        setFirstName("");
      } else {
        setSubscribeError(res.data.message || "Subscription failed");
      }
    } catch (err) {
      setSubscribeError("Something went wrong. Please try again.");
    } finally {
      setIsSubscribing(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-2xl p-5 sm:p-6 border border-amber-200 shadow-lg animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-200 rounded-full" />
          <div className="flex-1">
            <div className="h-4 bg-amber-200 rounded w-32 mb-2" />
            <div className="h-3 bg-amber-100 rounded w-20" />
          </div>
        </div>
        <div className="bg-white/60 rounded-xl p-4">
          <div className="h-4 bg-gray-200 rounded w-full mb-2" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </div>
      </div>
    );
  }
  
  if (!tip) {
    return null;
  }
  
  const dayNames = {
    en: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    fr: ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"]
  };
  
  const dayName = dayNames[language]?.[tip.day_of_week] || dayNames.en[tip.day_of_week];
  
  if (variant === "compact") {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-3 border border-amber-200 ${className}`}
        data-testid="daily-tip-compact"
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Lightbulb className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-amber-800 mb-1">
              {language === "fr" ? `Conseil du ${dayName}` : `${dayName}'s Tip`}
            </p>
            <p className="text-sm text-gray-700 line-clamp-2">{tip.text}</p>
            {tip.audio_url && (
              <button
                onClick={togglePlay}
                className={`mt-2 flex items-center gap-1.5 text-xs font-medium transition-colors ${
                  isPlaying ? "text-amber-600" : "text-gray-500 hover:text-amber-600"
                }`}
              >
                {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                {isPlaying 
                  ? (language === "fr" ? "En lecture..." : "Playing...") 
                  : (language === "fr" ? "Écouter" : "Listen")}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  }
  
  // Default: card variant with blog
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Daily Tip Card - Short and sweet */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-2xl p-5 sm:p-6 border border-amber-200 shadow-lg"
        data-testid="daily-tip-card"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center shadow-md">
              <Lightbulb className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                {tip.title || (language === "fr" ? "Conseil Rapide du Jour" : "Karine's Quick Tip")}
              </h3>
              <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} • {tip.audio_url ? "~1 min read" : "~15 sec"}
              </p>
            </div>
          </div>
          <Sparkles className="w-5 h-5 text-amber-400" />
        </div>
        
        {/* Tip Content */}
        <div className="bg-white/60 rounded-xl p-4 mb-4">
          <p className="text-gray-700 leading-relaxed">{tip.text}</p>
        </div>
        
        {/* Audio Player */}
        {tip.audio_url && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <img 
                src="/karine-headshot.jpg" 
                alt="Karine" 
                className="w-10 h-10 rounded-full object-cover object-top border-2 border-amber-300"
              />
              <button
                onClick={togglePlay}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-95 ${
                  isPlaying 
                    ? "bg-amber-500 text-white shadow-lg" 
                    : "bg-amber-200 text-amber-700 hover:bg-amber-300"
                }`}
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
            </div>
            
            <div className="flex-1">
              <p className="text-xs font-medium text-amber-800 mb-1">
                {isPlaying 
                  ? (language === "fr" ? "Karine parle..." : "Karine speaking...") 
                  : (language === "fr" ? "Écouter (~1 min)" : "Listen (~1 min)")}
              </p>
              <div className="h-1.5 bg-amber-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-amber-500 transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </motion.div>
      
      {/* Daily Blog Card - Road to Better Skin Health */}
      {showBlog && blog && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 rounded-2xl p-5 sm:p-6 border border-rose-200 shadow-lg"
          data-testid="daily-blog-card"
        >
          {/* Optional hero artwork — promotional posts like the Refer & Earn
              entry attach a `cover_image` to render an eye-catching banner
              above the title. Other posts render without it. */}
          {blog.cover_image && (
            <a
              href={blog.cta_url || "#"}
              {...(blog.cta_url && !blog.cta_url.startsWith("/") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className={`block mb-4 -mx-1 rounded-xl overflow-hidden border border-rose-200 shadow-md ${blog.cta_url ? "hover:shadow-lg transition-shadow" : ""}`}
              data-testid="daily-blog-cover-image"
            >
              <img
                src={blog.cover_image}
                alt={`${blog.title} — ${blog.subtitle || ""}`}
                className="w-full h-auto block select-none"
                loading="lazy"
              />
            </a>
          )}

          {/* Blog Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-purple-400 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-rose-500 uppercase tracking-wider mb-1">
                  {language === "fr" ? "Route Vers une Peau Plus Saine" : "Road to Better Skin Health"}
                </p>
                <h3 className="font-bold text-gray-900 text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {blog.title}
                </h3>
                <p className="text-sm text-gray-600 italic">{blog.subtitle}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-xs bg-rose-100 text-rose-600 px-2 py-1 rounded-full font-medium">
                {blog.category}
              </span>
              <span className="text-xs text-gray-500">{blog.read_time}</span>
            </div>
          </div>
          
          {/* Blog Content Preview/Full */}
          <div className="bg-white/60 rounded-xl p-4">
            {expandedBlog ? (
              <div className="prose prose-sm max-w-none text-gray-700">
                {blog.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="mb-3 last:mb-0 leading-relaxed whitespace-pre-wrap">
                    {paragraph.split('**').map((part, i) => 
                      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                    )}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-gray-700 leading-relaxed line-clamp-3">
                {blog.content.split('\n\n')[0]}
              </p>
            )}
          </div>
          
          {/* Promotional CTA — emerald button shown only when the blog has
              an explicit `cta_url` + `cta_label` (e.g. the Refer & Earn
              entry). Sits above the standard Read-More toggle. */}
          {blog.cta_url && blog.cta_label && (
            <a
              href={blog.cta_url}
              {...(blog.cta_url.startsWith("/") ? {} : { target: "_blank", rel: "noopener noreferrer" })}
              className="mt-4 w-full py-3 px-4 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 shadow-md hover:shadow-lg active:scale-[0.98] transition-all"
              data-testid="daily-blog-cta"
            >
              {blog.cta_label}
            </a>
          )}

          {/* Read More/Less Button */}
          <button
            onClick={() => setExpandedBlog(!expandedBlog)}
            className="mt-4 w-full py-2.5 bg-gradient-to-r from-rose-400 to-purple-400 text-white rounded-xl font-medium text-sm hover:from-rose-500 hover:to-purple-500 transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
          >
            {expandedBlog 
              ? (language === "fr" ? "Réduire" : "Show Less") 
              : (language === "fr" ? "Lire l'article complet" : "Read Full Article")}
          </button>
        </motion.div>
      )}
      
      {/* Email Subscription Card */}
      {showSubscribe && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-2xl p-5 sm:p-6 border border-emerald-200 shadow-lg"
          data-testid="daily-tips-subscribe-card"
        >
          {subscribeSuccess ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-400 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-xl text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {language === "fr" ? "Vous êtes inscrit(e)!" : "You're In!"}
              </h3>
              <p className="text-gray-600">
                {language === "fr" 
                  ? "Votre premier conseil arrive demain matin ✨" 
                  : "Your first tip arrives tomorrow morning ✨"}
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-400 rounded-full flex items-center justify-center shadow-md">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {language === "fr" ? "Conseils Quotidiens par Email" : "Karine's 1 Minute Skin Tip Daily"}
                  </h3>
                  <p className="text-xs text-emerald-600 font-medium">
                    {language === "fr" ? "Gratuit • Chaque matin" : "Free • Every Morning"}
                  </p>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm mb-4">
                {language === "fr"
                  ? "Recevez les conseils beauté de Karine directement dans votre boîte mail chaque matin. Des astuces rapides pour une peau radieuse!"
                  : "Get Karine's beauty tips delivered to your inbox every morning. Quick secrets for radiant, healthy skin!"}
              </p>
              
              {/* Subscription Form */}
              <form onSubmit={handleSubscribe} className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder={language === "fr" ? "Prénom (optionnel)" : "First name (optional)"}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-emerald-200 bg-white/80 focus:outline-none focus:ring-2 focus:ring-emerald-400 text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder={language === "fr" ? "Votre email" : "Your email address"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="flex-1 px-4 py-2.5 rounded-xl border border-emerald-200 bg-white/80 focus:outline-none focus:ring-2 focus:ring-emerald-400 text-sm"
                  />
                  <button
                    type="submit"
                    disabled={isSubscribing || !email}
                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium text-sm hover:from-emerald-600 hover:to-teal-600 transition-all shadow-md hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubscribing ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span className="hidden sm:inline">{language === "fr" ? "S'inscrire" : "Subscribe"}</span>
                      </>
                    )}
                  </button>
                </div>
                
                {subscribeError && (
                  <p className="text-red-500 text-xs">{subscribeError}</p>
                )}
              </form>
              
              <p className="text-xs text-gray-500 mt-3 text-center">
                {language === "fr" 
                  ? "Désabonnement facile à tout moment. Pas de spam, promis! 💛"
                  : "Unsubscribe anytime. No spam, promise! 💛"}
              </p>
            </>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default KarineDailyTip;
